// src/components/CheckoutFormChangeModal.jsx
//
// Superadmin-only modal to change the form-type label on an existing
// checkout record. In the roster-driven model, form-type is METADATA ONLY:
// it controls the PDF header label and dashboard pill color, but does NOT
// affect which sections appear on the record. Section relevance is driven
// by rosters at record-creation time.
//
// Calls the changeCheckoutForm Cloud Function.

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  CHECKOUT_FORMS,
  getFormConfig,
  KA_COLORS,
} from '../utils/checkoutHelpers';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 8,
    width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: {
    background: KA_COLORS.navy, color: '#fff',
    padding: '16px 20px', borderRadius: '8px 8px 0 0',
  },
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  headerSubtitle: { margin: '4px 0 0', fontSize: 13, opacity: 0.85 },
  body: { padding: 20 },
  studentBlock: {
    background: KA_COLORS.lightGray, borderRadius: 6,
    padding: 12, marginBottom: 16,
  },
  studentName: { fontSize: 15, fontWeight: 600, color: KA_COLORS.navy, margin: 0 },
  studentMeta: { fontSize: 12, color: '#666', margin: '4px 0 0' },
  fieldLabel: {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: '#333', marginBottom: 6,
  },
  select: {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4,
    fontSize: 14, marginBottom: 16, background: '#fff',
  },
  textarea: {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4,
    fontSize: 13, minHeight: 60, fontFamily: 'inherit',
    resize: 'vertical', boxSizing: 'border-box', marginBottom: 12,
  },
  infoNote: {
    padding: 10,
    background: '#E3F2FD',
    border: '1px solid #BBDEFB',
    borderRadius: 4,
    fontSize: 12,
    color: '#1565C0',
    lineHeight: 1.5,
    marginBottom: 12,
  },
  errorBox: {
    marginTop: 12, padding: 10,
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    borderRadius: 4, color: '#C62828', fontSize: 13,
  },
  footer: {
    padding: '14px 20px', borderTop: `1px solid ${KA_COLORS.borderGray}`,
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px', border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff', borderRadius: 4, cursor: 'pointer',
    fontSize: 14, color: '#333',
  },
  confirmBtn: (disabled) => ({
    padding: '8px 16px', border: 'none',
    background: disabled ? '#ccc' : KA_COLORS.red, color: '#fff',
    borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14, fontWeight: 600,
  }),
};

const CheckoutFormChangeModal = ({ record, onClose, onChanged }) => {
  const [newFormId, setNewFormId] = useState(record?.formType || '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isChanging = newFormId && newFormId !== record?.formType;

  if (!record) return null;

  const handleConfirm = async () => {
    if (!isChanging) {
      setError('Select a different form type, or cancel.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'changeCheckoutForm');
      await fn({
        recordId: record.id || `${record.schoolYear}_${record.studentId}`,
        newFormId,
        reason: reason.trim() || null,
      });
      if (onChanged) onChanged(newFormId);
    } catch (err) {
      console.error('changeCheckoutForm failed:', err);
      setError(err?.message || 'Failed to change the form type.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const currentForm = getFormConfig(record.formType);

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Change Form Type</h2>
          <p style={styles.headerSubtitle}>
            Update the form-type label for this record.
          </p>
        </div>

        <div style={styles.body}>
          <div style={styles.studentBlock}>
            <p style={styles.studentName}>{record.studentName || 'Unknown'}</p>
            <p style={styles.studentMeta}>
              {[record.grade, record.cohort, record.studentId && `SIS ${record.studentId}`]
                .filter(Boolean)
                .join('  •  ')}
              {currentForm && <> &nbsp;•&nbsp; Currently: <strong>{currentForm.shortLabel}</strong></>}
            </p>
          </div>

          <div style={styles.infoNote}>
            <strong>Note:</strong> Form type is now metadata only — it controls
            the PDF header and dashboard label but does <strong>not</strong>{' '}
            change which sections appear on this record. Section relevance is
            driven by rosters. To change the sections on a record, edit the
            relevant rosters or use the per-section "Mark N/A / Reactivate"
            controls in the detail view.
          </div>

          <label style={styles.fieldLabel} htmlFor="newFormId">
            New form type
          </label>
          <select
            id="newFormId"
            style={styles.select}
            value={newFormId}
            onChange={(e) => setNewFormId(e.target.value)}
            disabled={submitting}
          >
            {CHECKOUT_FORMS.map((form) => (
              <option key={form.id} value={form.id}>
                {form.label} {form.id === record.formType ? '(current)' : ''}
              </option>
            ))}
          </select>

          <label style={styles.fieldLabel} htmlFor="reason">
            Reason for change (optional, recorded in audit log)
          </label>
          <textarea
            id="reason"
            style={styles.textarea}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Re-classified as international after visa status confirmed."
            disabled={submitting}
          />

          {error && <div style={styles.errorBox}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            style={styles.confirmBtn(submitting || !isChanging)}
            onClick={handleConfirm}
            disabled={submitting || !isChanging}
          >
            {submitting ? 'Changing…' : 'Change Form Type'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutFormChangeModal;